import { FC } from "react";
import { Field, Form } from "react-final-form";
import { useLogeMembers } from "../../api/user-api";
import { EventStatus, LogeEvent } from "../../types/entities";
import { Field as DssField } from "../dss/Primitives";
import { Icon } from "../dss/Icon";

export interface EventFormValues {
  title: string;
  emoji: string;
  date: string;
  time: string;
  description?: string;
  status: EventStatus;
  showVoteLink: boolean;
  host: LogeEvent["host"];
}

interface EventFormProps {
  onSubmit: (values: EventFormValues) => void;
  initialValues?: Partial<EventFormValues>;
  submitLabel?: string;
  children?: React.ReactNode;
}

const STATUS_OPTIONS: { value: EventStatus; label: string }[] = [
  { value: "confirmed", label: "Bekræftet" },
  { value: "draft", label: "Kladde" },
  { value: "cancelled", label: "Aflyst" },
];

const FIELD_LABELS: Partial<Record<keyof EventFormValues, string>> = {
  title: "Titel",
  emoji: "Tegn",
  date: "Dato",
  time: "Klokkeslæt",
};

const validate = (values: Partial<EventFormValues>) => {
  const errors: Partial<Record<keyof EventFormValues, string>> = {};
  if (!values.title?.trim()) errors.title = "påkrævet";
  if (!values.emoji?.trim()) errors.emoji = "påkrævet";
  if (!values.date) errors.date = "påkrævet";
  if (!values.time) errors.time = "påkrævet";
  return errors;
};

const EventForm: FC<EventFormProps> = ({
  onSubmit,
  initialValues,
  submitLabel = "Gem",
  children,
}) => {
  const { data: members } = useLogeMembers();
  return (
    <Form<EventFormValues>
      initialValues={initialValues}
      onSubmit={onSubmit}
      validate={validate}
      render={({ handleSubmit, values, errors, submitFailed }) => (
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <DssField label="Titel">
            <Field name="title">
              {(props) => (
                <input
                  type="text"
                  className="w-full bg-transparent border-b-2 border-ink pb-2 font-display text-xl font-bold tracking-tight focus:outline-none"
                  {...props.input}
                />
              )}
            </Field>
          </DssField>

          <div className="grid grid-cols-2 gap-4">
            <DssField label="Dato">
              <Field name="date">
                {(props) => (
                  <input
                    type="date"
                    className="w-full bg-card border border-line-soft rounded-lg py-3 px-3.5 font-mono text-base"
                    {...props.input}
                  />
                )}
              </Field>
            </DssField>
            <DssField label="Kl.">
              <Field name="time">
                {(props) => (
                  <input
                    type="time"
                    className="w-full bg-card border border-line-soft rounded-lg py-3 px-3.5 font-mono text-base"
                    {...props.input}
                  />
                )}
              </Field>
            </DssField>
          </div>

          <DssField label="Vært">
            <div className="flex gap-2 flex-wrap">
              {members?.map((m) => (
                <Field key={m.id} name="host" type="radio" value={m.id} parse={(v) => Number(v)}>
                  {(props) => (
                    <label
                      className={`flex-1 min-w-[64px] py-2.5 rounded-lg border flex flex-col items-center gap-1 cursor-pointer ${
                        values.host === m.id
                          ? "bg-ink border-ink text-paper"
                          : "bg-card border-line-soft"
                      }`}
                    >
                      <input type="radio" className="sr-only" {...props.input} />
                      <span className="text-base">{m.emoji}</span>
                      <span className="font-mono text-[8.5px] tracking-wide uppercase">
                        {m.name}
                      </span>
                    </label>
                  )}
                </Field>
              ))}
            </div>
          </DssField>

          <DssField label="Tegn">
            <Field name="emoji">
              {(props) => (
                <input
                  type="text"
                  className="w-20 bg-card border border-line-soft rounded-lg py-2 px-3 text-2xl text-center leading-none"
                  {...props.input}
                />
              )}
            </Field>
          </DssField>

          <DssField label="Vis afstemningslink">
            <Field name="showVoteLink" type="checkbox">
              {(props) => (
                <button
                  type="button"
                  onClick={() => props.input.onChange(!props.input.checked)}
                  className={`w-[42px] h-6 rounded-full flex items-center px-0.5 border ${
                    props.input.checked ? "bg-vote-yes border-vote-yes justify-end" : "bg-paper2 border-line justify-start"
                  }`}
                >
                  <span className="w-[18px] h-[18px] rounded-full bg-paper" />
                </button>
              )}
            </Field>
          </DssField>

          <DssField label="Status">
            <div className="flex gap-1.5">
              {STATUS_OPTIONS.map((o) => (
                <Field key={o.value} name="status" type="radio" value={o.value}>
                  {(props) => (
                    <label
                      className={`flex-1 text-center py-2.5 rounded-lg border font-mono text-[10px] tracking-wide uppercase cursor-pointer ${
                        values.status === o.value
                          ? "bg-ink border-ink text-paper"
                          : "bg-transparent border-line-soft text-ink3"
                      }`}
                    >
                      <input type="radio" className="sr-only" {...props.input} />
                      {o.label}
                    </label>
                  )}
                </Field>
              ))}
            </div>
          </DssField>

          {submitFailed && Object.keys(errors ?? {}).length > 0 && (
            <div className="font-mono text-[11px] text-clay uppercase tracking-wide">
              Mangler: {Object.keys(errors ?? {})
                .map((key) => FIELD_LABELS[key as keyof EventFormValues] ?? key)
                .join(", ")}
            </div>
          )}

          <button
            type="submit"
            className="w-full py-4 rounded-lg bg-ink text-paper font-display text-sm font-bold tracking-wide uppercase flex items-center justify-center gap-2"
          >
            <Icon name="circle" size={7} />
            {submitLabel}
          </button>

          {children}
        </form>
      )}
    />
  );
};

export default EventForm;
